import { useEffect, useMemo, useState } from 'react'

import { getFileDownloadUrl, getFileList } from '../../helpers/api'
import type { BuilderCustomComponentProps } from '../../types/builderio-ids'
import type { RegulatoryFile } from '../../types/regulatoryFiles'
import {
  Caret,
  Component,
  Content,
  DocIcon,
  FileDate,
  FileLink,
  FileList,
  FileRow,
  FileTitle,
  FilterBar,
  FilterLabel,
  Intro,
  Layout,
  LoadMore,
  LoadMoreWrapper,
  Message,
  Select,
  SelectWrapper,
  Sidebar,
  TabButton,
} from './regulatoryFileBrowser.styles'

interface RegulatoryTab {
  label: string
  section: string
  filterable?: boolean
  openInNewTab?: boolean
  introSentence?: string
}

interface RegulatoryFileBrowserProps extends BuilderCustomComponentProps {
  tabs?: RegulatoryTab[]
  years?: { year: string }[]
  initialRows?: number
}

const ALL_MONTHS = 'all'

const MONTHS: { value: string; label: string }[] = [
  { value: ALL_MONTHS, label: 'All Months' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

const formatDate = (iso: string): string => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

// Only the calendar day is shown in the list, so files published on the same
// day count as a tie and fall back to the name.
const publishDay = (iso: string): string => (iso || '').slice(0, 10)

const RegulatoryFileBrowser = ({
  tabs = [],
  years = [{ year: '2026' }],
  initialRows = 25,
  attributes,
}: RegulatoryFileBrowserProps) => {
  const yearOptions = years.map((y) => y.year).filter(Boolean)
  const defaultYear = yearOptions[0] || '2026'

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [year, setYear] = useState(defaultYear)
  const [month, setMonth] = useState(ALL_MONTHS)
  const [files, setFiles] = useState<RegulatoryFile[]>([])
  const [loading, setLoading] = useState(true)
  const [visibleCount, setVisibleCount] = useState(initialRows)

  const activeTab = tabs[activeTabIndex]

  useEffect(() => {
    if (!activeTab?.section) {
      setFiles([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    const filters = activeTab.filterable ? { year, ...(month !== ALL_MONTHS ? { month } : {}) } : {}

    getFileList(activeTab.section, filters).then((result) => {
      if (cancelled) return
      setFiles(result.files)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [activeTab?.section, activeTab?.filterable, year, month])

  const selectTab = (index: number) => {
    setActiveTabIndex(index)
    setYear(defaultYear)
    setMonth(ALL_MONTHS)
    setVisibleCount(initialRows)
  }

  // The backend returns the files in listing order, so the default order here is
  // newest publish date first, and by name inside the same date.
  const sortedFiles = useMemo(
    () =>
      [...files].sort((a, b) => {
        const byDate = publishDay(b.lastModified).localeCompare(publishDay(a.lastModified))
        if (byDate !== 0) return byDate
        return a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' })
      }),
    [files],
  )

  if (tabs.length === 0) return null

  // A single selected month shows every file for that month; otherwise page with Load More.
  const isSingleMonth = Boolean(activeTab?.filterable) && month !== ALL_MONTHS
  const visibleFiles = isSingleMonth ? sortedFiles : sortedFiles.slice(0, visibleCount)
  const canLoadMore = !isSingleMonth && sortedFiles.length > visibleCount

  return (
    <Component {...attributes}>
      <Layout>
        <Sidebar>
          {tabs.map((tab, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Using index as key is acceptable here because the tabs are static and do not change order.
            <TabButton key={index} type="button" $active={index === activeTabIndex} onClick={() => selectTab(index)}>
              {tab.label}
            </TabButton>
          ))}
        </Sidebar>

        <Content>
          {activeTab?.filterable && (
            <>
              {activeTab.introSentence && <Intro>{activeTab.introSentence}</Intro>}
              <FilterBar>
                <FilterLabel>Filter</FilterLabel>
                <SelectWrapper $width="11.6rem">
                  <Select
                    aria-label="Filter by year"
                    value={year}
                    onChange={(event) => {
                      setYear(event.target.value)
                      setVisibleCount(initialRows)
                    }}
                  >
                    {yearOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <Caret viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5 7l5 6 5-6z" />
                  </Caret>
                </SelectWrapper>
                <SelectWrapper $width="14.8rem">
                  <Select
                    aria-label="Filter by month"
                    value={month}
                    onChange={(event) => {
                      setMonth(event.target.value)
                      setVisibleCount(initialRows)
                    }}
                  >
                    {MONTHS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Caret viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5 7l5 6 5-6z" />
                  </Caret>
                </SelectWrapper>
              </FilterBar>
            </>
          )}

          {loading ? (
            <Message>Loading files…</Message>
          ) : visibleFiles.length === 0 ? (
            <Message>No files available.</Message>
          ) : (
            <FileList>
              {visibleFiles.map((file) => (
                <FileRow key={file.key}>
                  <FileLink
                    href={getFileDownloadUrl(activeTab.section, file.key)}
                    {...(activeTab.openInNewTab
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : { download: file.name })}
                  >
                    <FileTitle>{file.name}</FileTitle>
                    <FileDate>{formatDate(file.lastModified)}</FileDate>
                    <DocIcon viewBox="0 0 20 20" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14.1697 0.550674L17.4464 3.82738C17.7979 4.17887 17.9971 4.65925 17.9971 5.15524V18.1254C17.9971 19.1603 17.1574 20 16.1224 20H4.87463C3.83968 20 3 19.1603 3 18.1254V1.87854C3 0.843585 3.83968 0.00390549 4.87463 0H12.8457C13.3417 0 13.8182 0.19918 14.1697 0.550674ZM16.7278 5.00293C16.7004 4.89358 16.6458 4.79594 16.5638 4.71393L13.287 1.43722C13.205 1.35521 13.1074 1.30053 12.998 1.27319V5.00293H16.7278ZM16.1224 18.7502H4.87463C4.53095 18.7502 4.24975 18.469 4.24975 18.1254V1.87854C4.24975 1.53486 4.53095 1.25366 4.87463 1.25366H11.7483V5.31537C11.7483 5.8348 12.1662 6.25269 12.6856 6.25269H16.7473V18.1254C16.7473 18.469 16.4661 18.7502 16.1224 18.7502ZM14.2478 9.53329V9.22085C14.2478 8.96309 14.0369 8.75219 13.7791 8.75219H7.21793C6.96017 8.75219 6.74927 8.96309 6.74927 9.22085V9.53329C6.74927 9.79105 6.96017 10.0019 7.21793 10.0019H13.7791C14.0369 10.0019 14.2478 9.79105 14.2478 9.53329ZM14.2478 11.7204V12.0328C14.2478 12.2906 14.0369 12.5015 13.7791 12.5015H7.21793C6.96017 12.5015 6.74927 12.2906 6.74927 12.0328V11.7204C6.74927 11.4626 6.96017 11.2517 7.21793 11.2517H13.7791C14.0369 11.2517 14.2478 11.4626 14.2478 11.7204ZM14.2478 14.5323V14.2199C14.2478 13.9621 14.0369 13.7512 13.7791 13.7512H7.21793C6.96017 13.7512 6.74927 13.9621 6.74927 14.2199V14.5323C6.74927 14.7901 6.96017 15.001 7.21793 15.001H13.7791C14.0369 15.001 14.2478 14.7901 14.2478 14.5323Z"
                      />
                    </DocIcon>
                  </FileLink>
                </FileRow>
              ))}
            </FileList>
          )}

          {canLoadMore && (
            <LoadMoreWrapper>
              <LoadMore type="button" onClick={() => setVisibleCount((count) => count + initialRows)}>
                Load more
              </LoadMore>
            </LoadMoreWrapper>
          )}
        </Content>
      </Layout>
    </Component>
  )
}

export default RegulatoryFileBrowser
